import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { usePortalAuth } from '../context/PortalAuthContext';
import { PortalAuthAPI } from '../services/portalApi';

export const PortalLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { stage, setPasskeyToken, setFullToken } = usePortalAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already fully authenticated, redirect
  React.useEffect(() => {
    if (stage === 'fully_authenticated') navigate('/ip-portal/dashboard', { replace: true });
  }, [stage, navigate]);

  const handleEmailSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      // Step 1: Get WebAuthn challenge
      const { data: options } = await PortalAuthAPI.loginStart({ email });

      // Step 2: Start passkey authentication
      const credential = await startAuthentication({ optionsJSON: options });

      // Step 3: Verify with backend
      const { data } = await PortalAuthAPI.loginFinish({ email, credential });
      setPasskeyToken(data.token);
      setStep(3);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Authentication failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await PortalAuthAPI.loginTotp({ code: totpCode });
      setFullToken(data.token);
      navigate('/ip-portal/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid TOTP code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portal-center-page">
      <div className="portal-center-box">
        <div className="portal-card">
          <h2>Portal Login</h2>

          <div className="portal-steps">
            <div className={`portal-step ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`}>
              <span className="portal-step-num">1</span> Email
            </div>
            <div className={`portal-step ${step >= 2 ? (step > 2 ? 'done' : 'active') : ''}`}>
              <span className="portal-step-num">2</span> Passkey
            </div>
            <div className={`portal-step ${step >= 3 ? 'active' : ''}`}>
              <span className="portal-step-num">3</span> TOTP
            </div>
          </div>

          {error && <div className="portal-alert portal-alert-error">{error}</div>}

          {step === 1 && (
            <>
              <div className="portal-form-group">
                <label>Email</label>
                <input
                  className="portal-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  onKeyDown={(e) => e.key === 'Enter' && email && handleEmailSubmit()}
                  autoFocus
                />
              </div>
              <button
                className="portal-btn portal-btn-primary"
                onClick={handleEmailSubmit}
                disabled={!email || loading}
              >
                {loading ? 'Authenticating...' : 'Continue with Passkey'}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div className="portal-alert portal-alert-info">
                Passkey verified! Enter your TOTP code to complete login.
              </div>
              <div className="portal-form-group">
                <label>TOTP Code</label>
                <input
                  className="portal-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  onKeyDown={(e) => e.key === 'Enter' && totpCode.length === 6 && handleTotpSubmit()}
                  autoFocus
                />
              </div>
              <button
                className="portal-btn portal-btn-primary"
                onClick={handleTotpSubmit}
                disabled={totpCode.length !== 6 || loading}
              >
                {loading ? 'Verifying...' : 'Verify TOTP'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
